package br.com.htms.inspeccaocampo.platform

import br.com.htms.inspeccaocampo.util.Base64Codec
import br.com.htms.inspeccaocampo.util.ImageHeaders
import kotlin.coroutines.resume
import kotlinx.browser.document
import kotlinx.coroutines.suspendCancellableCoroutine
import org.khronos.webgl.Uint8Array
import org.khronos.webgl.set
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag

actual object PlatformImage {
    private const val MAX_EDGE = 1600.0

    actual suspend fun applyStamp(path: String, dados: PhotoStampData): ByteArray? {
        val bytes = PlatformFiles.readBytes(path) ?: PlatformFiles.readBytesAsync(path)
        if (bytes == null || bytes.isEmpty()) {
            consoleWarn("applyStamp: sem bytes em $path")
            return null
        }
        if (isLikelyHeic(bytes)) {
            consoleWarn("applyStamp: HEIC não suportado no browser — tire a foto pela câmera (JPEG)")
            return null
        }
        val out = stampBytesToJpeg(bytes, dados)
        if (out == null) {
            consoleWarn("applyStamp: falha ao carimbar/decodificar")
            return null
        }
        // Memória + IDB — NÃO dispara download (cancelar o diálogo quebrava o carimbo).
        val ok = PlatformFiles.writeBytesDurable(path, out)
        if (!ok) {
            consoleWarn("applyStamp: IDB falhou; bytes ficaram só em memória ($path, ${out.size}b)")
        } else {
            consoleWarn("applyStamp: ok ($path, ${out.size}b)")
        }
        return out
    }

    private suspend fun stampBytesToJpeg(bytes: ByteArray, dados: PhotoStampData): ByteArray? =
        suspendCancellableCoroutine { cont ->
            val uint8 = Uint8Array(bytes.size)
            bytes.forEachIndexed { i, b -> uint8[i] = b }
            val mime = mimeOf(bytes)
            val blob = Blob(arrayOf(uint8), BlobPropertyBag(type = mime))
            val url = URL.createObjectURL(blob)
            val img = document.createElement("img") as HTMLImageElement
            img.onload = {
                try {
                    val srcW = img.naturalWidth.coerceAtLeast(1)
                    val srcH = img.naturalHeight.coerceAtLeast(1)
                    // Mobile (esp. iOS Safari): canvas full-res estoura memória e toDataURL
                    // devolve data URL vazia — carimbo “ok” com bytes inválidos e PDF sem foto.
                    val scale = minOf(1.0, MAX_EDGE / maxOf(srcW, srcH).toDouble())
                    val w = (srcW * scale).toInt().coerceAtLeast(1)
                    val h = (srcH * scale).toInt().coerceAtLeast(1)
                    val canvas = document.createElement("canvas") as HTMLCanvasElement
                    canvas.width = w
                    canvas.height = h
                    val ctx = canvas.getContext("2d") as CanvasRenderingContext2D
                    ctx.fillStyle = "#FFFFFF"
                    ctx.fillRect(0.0, 0.0, w.toDouble(), h.toDouble())
                    ctx.drawImage(img, 0.0, 0.0, w.toDouble(), h.toDouble())
                    drawStamp(ctx, w, h, dados)
                    val dataUrl = canvas.toDataURL("image/jpeg", 0.88)
                    if (!dataUrl.contains("base64,")) {
                        consoleWarn("applyStamp: toDataURL sem base64 (canvas grande?)")
                        if (cont.isActive) cont.resume(null)
                    } else {
                        val b64 = dataUrl.substringAfter("base64,")
                        val out = Base64Codec.decode(b64)
                        if (isValidJpeg(out)) {
                            if (cont.isActive) cont.resume(out)
                        } else {
                            consoleWarn(
                                "applyStamp: JPEG inválido após carimbo " +
                                    "(size=${out?.size ?: 0})"
                            )
                            if (cont.isActive) cont.resume(null)
                        }
                    }
                } catch (t: dynamic) {
                    consoleWarn("applyStamp: $t")
                    if (cont.isActive) cont.resume(null)
                } finally {
                    URL.revokeObjectURL(url)
                }
            }
            img.addEventListener("error", {
                URL.revokeObjectURL(url)
                consoleWarn(
                    "applyStamp: falha ao decodificar imagem (mime=$mime). " +
                        "Se for HEIC do iPhone, use a câmera ou exporte como JPG."
                )
                if (cont.isActive) cont.resume(null)
            })
            img.src = url
        }

    actual fun imageSize(bytes: ByteArray) = ImageHeaders.size(bytes)

    actual suspend fun toJpegBytes(
        bytes: ByteArray,
        quality: Float,
        flattenRgb: Int?,
        maxEdgePx: Int,
        forceReencode: Boolean
    ): ByteArray? {
        val q = quality.coerceIn(0.5f, 0.95f).toDouble()
        val edgeLimit = maxEdgePx.coerceIn(640, 4096).toDouble()
        // Mesmo se já for JPEG, reencoda/redimensiona se for grande demais para o PDF mobile.
        val size = ImageHeaders.size(bytes)
        val alreadyJpeg = isJpegMagic(bytes)
        if (alreadyJpeg && flattenRgb == null && !forceReencode) {
            if (size == null) {
                // SOF ilegível (EXIF estranho etc.) — ainda assim devolve o JPEG cru;
                // o PDF tenta imageSize/canvas em seguida. NÃO forçar canvas aqui
                // (falha silenciosa no iOS Safari = foto some do PDF).
                return bytes
            }
            val maxEdge = maxOf(size.width, size.height)
            if (maxEdge <= edgeLimit) return bytes
        }
        if (isLikelyHeic(bytes)) {
            consoleWarn("toJpegBytes: HEIC sem suporte no browser")
            return null
        }
        return encodeViaCanvas(bytes, q, flattenRgb, edgeLimit)
            ?: bytes.takeIf { alreadyJpeg && isValidJpeg(bytes) }
            ?: bytes.takeIf { alreadyJpeg }
    }

    actual suspend fun toSignatureJpegBytes(bytes: ByteArray, quality: Float): ByteArray? {
        val q = quality.coerceIn(0.5f, 0.95f)
        // Sem varredura pixel a pixel em Kotlin/JS — assinaturas grandes congelavam o portal.
        // Fundo branco + Multiply no PDF remove a margem.
        return toJpegBytes(bytes, q, flattenRgb = 0xFFFFFF)
    }

    /**
     * Na web NÃO pedimos câmera/GPS no boot.
     * getUserMedia/geolocation sem gesto do usuário ficam pendentes (prompt invisível
     * ou bloqueado) e travam o splash em fundo HTMS azul — parece “tela em branco”.
     * Permissões são pedidas sob demanda (foto / botão GPS).
     */
    actual suspend fun requestPermissions(): Boolean = true

    private fun cssRgb(rgb: Int?): String {
        if (rgb == null) return "#FFFFFF"
        val r = (rgb shr 16) and 0xFF
        val g = (rgb shr 8) and 0xFF
        val b = rgb and 0xFF
        return "rgb($r,$g,$b)"
    }

    private suspend fun encodeViaCanvas(
        bytes: ByteArray,
        quality: Double,
        flattenRgb: Int? = null,
        maxEdge: Double = MAX_EDGE
    ): ByteArray? =
        suspendCancellableCoroutine { cont ->
            val uint8 = Uint8Array(bytes.size)
            bytes.forEachIndexed { i, b -> uint8[i] = b }
            val mime = mimeOf(bytes)
            val blob = Blob(arrayOf(uint8), BlobPropertyBag(type = mime))
            val url = URL.createObjectURL(blob)
            val img = document.createElement("img") as HTMLImageElement
            img.onload = {
                try {
                    val srcW = img.naturalWidth.coerceAtLeast(1)
                    val srcH = img.naturalHeight.coerceAtLeast(1)
                    val scale = minOf(1.0, maxEdge / maxOf(srcW, srcH).toDouble())
                    val w = (srcW * scale).toInt().coerceAtLeast(1)
                    val h = (srcH * scale).toInt().coerceAtLeast(1)
                    val canvas = document.createElement("canvas") as HTMLCanvasElement
                    canvas.width = w
                    canvas.height = h
                    val ctx = canvas.getContext("2d") as CanvasRenderingContext2D
                    // JPEG não tem alpha: fundo navy (banner) ou branco evita contorno.
                    ctx.fillStyle = cssRgb(flattenRgb)
                    ctx.fillRect(0.0, 0.0, w.toDouble(), h.toDouble())
                    ctx.drawImage(img, 0.0, 0.0, w.toDouble(), h.toDouble())
                    val dataUrl = canvas.toDataURL("image/jpeg", quality)
                    val b64 = dataUrl.substringAfter("base64,", "")
                    val out = if (b64.isBlank()) null else Base64Codec.decode(b64)
                    val ok = isValidJpeg(out)
                    if (cont.isActive) cont.resume(if (ok) out else null)
                } catch (t: dynamic) {
                    consoleWarn("encodeViaCanvas: $t")
                    if (cont.isActive) cont.resume(null)
                } finally {
                    URL.revokeObjectURL(url)
                }
            }
            img.addEventListener("error", {
                URL.revokeObjectURL(url)
                consoleWarn("encodeViaCanvas: decode falhou (mime=$mime)")
                if (cont.isActive) cont.resume(null)
            })
            img.src = url
        }

    private fun drawStamp(
        ctx: CanvasRenderingContext2D,
        w: Int,
        h: Int,
        dados: PhotoStampData
    ) {
        val baseLinhas = buildList {
            add("HTMS ENERGIA")
            add(dados.numeroInspecao?.let { "Inspecao n $it" } ?: "Inspecao em preenchimento")
            add(formatDateTimeBr(dados.dataHoraMs))
            val lat = dados.latitude
            val lng = dados.longitude
            add(
                if (lat != null && lng != null) formatGpsCoordenadas(lat, lng)
                else "GPS nao capturado"
            )
            add("${dados.rotuloResponsavel}: ${dados.tstResponsavel.ifBlank { "-" }}")
            add("Responsavel frente: ${dados.responsavelFrente.ifBlank { "-" }}")
            add("Local: ${dados.localTexto.ifBlank { "-" }.replace('\n', ' ')}")
            add(dados.pergunta)
            add("Resposta: ${dados.resposta}")
        }
        // Compacto no canto inferior direito (estilo APK), não faixa larga.
        val textSize = maxOf(14.0, w * 0.024)
        val pad = textSize * 0.55
        val lineH = textSize * 1.15
        val margem = textSize * 0.7
        val maxBoxW = w * 0.55
        val maxTextW = maxBoxW - pad * 2
        val maxChars = (maxTextW / (textSize * 0.55)).toInt().coerceAtLeast(12)
        val linhas = TextBoxWrap.wrapAll(baseLinhas, maxChars)
        ctx.font = "bold ${textSize}px sans-serif"
        var textW = 0.0
        linhas.forEach { line ->
            textW = maxOf(textW, ctx.measureText(line).width)
        }
        val boxW = (textW + pad * 2).coerceAtMost(maxBoxW)
        var boxH = pad * 2 + lineH * linhas.size
        val maxBoxH = h * 0.28
        val scale = if (boxH > maxBoxH && boxH > 0) (maxBoxH / boxH).coerceIn(0.55, 1.0) else 1.0
        val ts = textSize * scale
        val pd = pad * scale
        val lh = lineH * scale
        val bw = boxW * scale
        boxH *= scale
        val left = (w - margem - bw).coerceAtLeast(margem)
        val top = (h - margem - boxH).coerceAtLeast(margem)
        ctx.fillStyle = "rgba(0,0,0,0.62)"
        // Canvas 2D não tem roundRect em todos os Safari antigos — retângulo simples.
        ctx.fillRect(left, top, bw, boxH)
        ctx.fillStyle = "#FFFFFF"
        ctx.font = "bold ${ts}px sans-serif"
        var y = top + pd + ts * 0.85
        linhas.forEach { line ->
            ctx.fillText(line, left + pd, y)
            y += lh
        }
    }

    private fun mimeOf(bytes: ByteArray): String = when {
        isJpegMagic(bytes) -> "image/jpeg"
        bytes.size >= 4 && bytes[0] == 0x89.toByte() && bytes[1] == 'P'.code.toByte() -> "image/png"
        isLikelyHeic(bytes) -> "image/heic"
        else -> "application/octet-stream"
    }

    private fun isLikelyHeic(bytes: ByteArray): Boolean =
        bytes.size >= 12 &&
            bytes[4] == 'f'.code.toByte() &&
            bytes[5] == 't'.code.toByte() &&
            bytes[6] == 'y'.code.toByte() &&
            bytes[7] == 'p'.code.toByte() &&
            (bytes[8] == 'h'.code.toByte() || bytes[8] == 'm'.code.toByte())

    private fun isJpegMagic(bytes: ByteArray): Boolean =
        bytes.size > 2 && bytes[0] == 0xFF.toByte() && bytes[1] == 0xD8.toByte()

    private fun isValidJpeg(bytes: ByteArray?): Boolean {
        if (bytes == null || bytes.size < 128) return false
        if (!isJpegMagic(bytes)) return false
        return ImageHeaders.size(bytes) != null
    }

    private fun consoleWarn(msg: String) {
        try {
            js("console").warn("[ICD Image] $msg")
        } catch (_: Throwable) {
        }
    }
}
